// A stage's result: how the stage started for the reader against how it
// ended, both as a standing (`winning` at the Winning setting or more, `lost`
// at minus that or less, `level` between). One grid serves every stage:
//
//   start \ end    winning    level      lost
//   winning        up         blown      blown
//   level          up         even       down
//   lost           comeback   comeback   down
//
// Each stage names the cells its own way: the ending (whose end is the
// game's outcome) Won · Comeback · Drawn · Blown win · Lost (`result.ts`), the
// middlegame Ahead · Comeback · Level · Blown advantage · Behind
// (`middlegame-result.ts`). Pure: the caller supplies the scores.
import type { GameOutcome, StageStanding } from '@chess/api-model';

/** A cell of the grid, before a stage names it. */
export type StageCell = 'up' | 'comeback' | 'even' | 'blown' | 'down';

/** How a position stood for the reader, from their score in pawns; undefined without a score. */
export function standingOf(pawns: number | undefined, winningPawns: number): StageStanding | undefined {
  if (pawns === undefined || Number.isNaN(pawns)) return undefined;
  if (pawns >= winningPawns) return 'winning';
  if (pawns <= -winningPawns) return 'lost';
  return 'level';
}

/** A game's outcome as a standing: won → winning, drawn → level, lost → lost. */
export function standingOfOutcome(outcome: GameOutcome): StageStanding {
  return outcome === 'win' ? 'winning' : outcome === 'loss' ? 'lost' : 'level';
}

const GRID: Record<StageStanding, Record<StageStanding, StageCell>> = {
  winning: { winning: 'up', level: 'blown', lost: 'blown' },
  level: { winning: 'up', level: 'even', lost: 'down' },
  lost: { winning: 'comeback', level: 'comeback', lost: 'down' },
};

/** The grid cell of a stage that started at `start` and ended at `end`. */
export function stageCellOf(start: StageStanding, end: StageStanding): StageCell {
  return GRID[start][end];
}
